import http, { request } from "@config/http";
import { Page, Pagination, List } from "@services/common/model/page.model";

import {
  ViewerPayloadModel,
  SequenceFileModel,
  SequenceFileUpdateModel,
  SequenceViewerQueryModel,
  SequenceViewerModel,
  CheckViewerNameModel,
  AlignFilePayloadModel,
  AlignFileModel,
  AlignFileResultModel,
  AlignInfoPayloadModel,
  AlignInfoModel,
  MsaSearchPayloadModel,
  MsaSearchResultModel
} from "@services/sequence-viewer/model/sequence-viewer.model";
import {
  UniProtResultModel,
  RcsbResultMoel,
  EntryResultModel
} from "@services/sequence-viewer/model/annotation.model";

export const createSequenceViewerApi = async (params: ViewerPayloadModel) => {
  return http.post<null, Page<{ id: string }>>("sequence_viewer/create", {
    ...params
  });
};

export const createSequenceFileApi = async (params: SequenceFileModel) => {
  const { data } = await http.post<null, Page<{ file_id: string }>>(
    "sequence_viewer/save_new_file_content",
    {
      ...params
    }
  );

  return data.file_id;
};

export const updateSequenceFileApi = async (
  params: SequenceFileUpdateModel
) => {
  const { data } = await http.post<null, Page<{ file_id: string }>>(
    "sequence_viewer/update_file_content",
    {
      ...params
    }
  );

  return data.file_id;
};

export const getSequenceViewerListApi = async (
  params: SequenceViewerQueryModel
) => {
  const { data } = await http.get<null, Page<Pagination<SequenceViewerModel>>>(
    "sequence_viewer/list",
    {
      params
    }
  );
  return data;
};

export const checkViewerNameApi = async (params: CheckViewerNameModel) => {
  return request.post<null, Page<null>>("sequence_viewer/check_name", {
    ...params
  });
};

export const renameViewerApi = async (params: CheckViewerNameModel) => {
  return http.post<null, Page<null>>("sequence_viewer/rename", {
    ...params
  });
};

export const deleteViewerApi = async (sequence_viewer_id: string) => {
  return http.delete<null, Page<null>>(`sequence_viewer/delete`, {
    params: {
      sequence_viewer_id
    }
  });
};

export const getSequenceViewerDetailApi = async (
  sequence_viewer_id: string
) => {
  const { data } = await http.get<null, Page<SequenceViewerModel>>(
    "sequence_viewer/one_detail",
    {
      params: {
        sequence_viewer_id
      }
    }
  );
  return data;
};

export const createAlignFileApi = async (params: AlignFilePayloadModel) => {
  const { data } = await http.post<null, Page<{ file_id: string }>>(
    "sequence_viewer/create_file_by_hash_value",
    {
      ...params
    }
  );

  return data.file_id;
};

export const getAlignFileApi = async (params: AlignFileModel) => {
  const { data } = await http.get<null, Page<AlignFileResultModel>>(
    "sequence_viewer/get_file_by_hash_value",
    {
      params
    }
  );
  return data;
};

export const getAlignInfoApi = async (
  params: AlignInfoPayloadModel,
  signal?: AbortSignal
) => {
  const { data } = await http.post<null, Page<AlignInfoModel>>(
    "sequence_viewer/align/align_by_engine",
    {
      ...params
    },
    {
      signal
    }
  );

  return data;
};

export const searchMsaServerApi = async (
  params: MsaSearchPayloadModel,
  signal?: AbortSignal
) => {
  const { data } = await http.post<null, Page<List<MsaSearchResultModel>>>(
    "sequence_viewer/search/msa_search",
    {
      ...params
    },
    {
      signal
    }
  );

  return data;
};

export const getUniProtAnnotationApi = async (
  accession: string,
  signal?: AbortSignal
) => {
  const data = await request.get<null, UniProtResultModel>(
    `https://rest.uniprot.org/uniprotkb/${accession}.json`,
    { signal }
  );
  return data;
};

export const batchGetUniProtAnnotationApi = async (
  accessions: string[],
  signal?: AbortSignal
) => {
  const query = accessions.map((id) => `accession:${id}`).join(" OR ");

  const url =
    `https://rest.uniprot.org/uniprotkb/stream?` +
    new URLSearchParams({
      query,
      format: "json"
    }).toString();

  const data = await request.get<null, { results: UniProtResultModel[] }>(url, {
    signal
  });
  return data.results;
};

export const getPdbAnnotationApi = async (
  ids: string[],
  signal?: AbortSignal
) => {
  const query = `
    query GetPolymerEntities($ids: [String!]!) {
      polymer_entity_instances(instance_ids: $ids) {
        rcsb_id

        polymer_entity {
          entity_poly {
            pdbx_seq_one_letter_code_can
          }
        }

        rcsb_polymer_entity_instance_container_identifiers {
          asym_id
          auth_asym_id
          entity_id
        }

        rcsb_polymer_instance_feature {
          feature_id
          type
          name
          provenance_source
          assignment_version
          reference_scheme
          additional_properties {
            name
            values
          }
          feature_positions {
            beg_seq_id
            end_seq_id
          }
          description
        }
      }
    }
  `;

  const { data } = await request.post<null, Page<RcsbResultMoel>>(
    "https://data.rcsb.org/graphql",
    {
      query,
      variables: {
        ids
      }
    },
    {
      signal
    }
  );

  return data.polymer_entity_instances;
};

export const batchGetEntryInfoApi = async (
  entryIds: string[],
  signal?: AbortSignal
) => {
  const query = `
    query getChainMapping($entryIds: [String!]!) {
      entries(entry_ids: $entryIds) {
        rcsb_id

        polymer_entities {
          rcsb_polymer_entity {
            pdbx_description
          }
          polymer_entity_instances {
            rcsb_id

            rcsb_polymer_entity_instance_container_identifiers {
              auth_asym_id
              asym_id
              entity_id
              entry_id
            }
          }
        }
      }
    }
  `;

  const { data } = await request.post<null, Page<EntryResultModel>>(
    "https://data.rcsb.org/graphql",
    {
      query,
      variables: {
        entryIds
      }
    },
    {
      signal
    }
  );

  return data.entries;
};
